/**
 * The Targeting tab's segment charts (spec §3.4, FR-D2-11): Share of spend,
 * CTR and CPC per targeting family, as three horizontal bar charts side by
 * side (one column on a phone).
 * - A family under 500 impressions has no CTR or CPC (null, R17). It is left
 *   out of those two charts, never drawn as a 0 bar, and one line says why.
 *   A family left out for another reason (a CPC over zero clicks, a CTR with
 *   no impressions reported) is left out the same way, but the line names the
 *   500-impression rule only for a family it is true of (lowSample).
 * - Each chart has one series, so there is no legend: the caption names it.
 * - Every bar carries its value at its tip, so there is no value axis either.
 *
 * Data attributes the live check reads: data-segment-chart, data-families.
 *
 * @module components/channels/SegmentCharts
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_PROPS, TOOLTIP_PROPS, VALUE_LABEL_PROPS } from '@/components/looker/charts/chartTheme';
import { colorAt } from '@/components/charts/chartPalette';
import { fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import { familyLabel, type TFn } from '@/services/channels/channelFormat';
import type { SegmentPoint, TargetingFamily } from '@/services/channels/channelTypes';

type SegmentMetric = 'share' | 'ctr' | 'cpc';

/** One bar: a family's value in one chart. A null value is no bar. */
interface SegmentBar {
  family: TargetingFamily;
  label: string;
  value: number;
}

/** Five families at most: room for every bar and its tip label. */
const SEGMENT_CHART_HEIGHT = 200;

const barsOf = (segments: readonly SegmentPoint[], metric: SegmentMetric, t: TFn): SegmentBar[] =>
  segments.flatMap((s) => {
    const value = s[metric];
    return value === null ? [] : [{ family: s.family, label: familyLabel(s.family, t), value }];
  });

interface ChartProps {
  metric: SegmentMetric;
  title: string;
  bars: SegmentBar[];
  format: (n: number | null) => string;
}

const SegmentChart: React.FC<ChartProps> = ({ metric, title, bars, format }) => {
  const text = (v: unknown): string => format(typeof v === 'number' ? v : null);
  return (
    <figure data-segment-chart={metric} data-families={bars.map((b) => b.family).join(',')} className="min-w-0">
      <figcaption className="mb-1 text-xs font-medium text-neutral-800">{title}</figcaption>
      <div style={{ height: SEGMENT_CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" {...AXIS_PROPS} width={96} />
            <Tooltip {...TOOLTIP_PROPS} formatter={text} />
            <Bar dataKey="value" name={title} fill={colorAt(0)} radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
              <LabelList dataKey="value" position="right" formatter={text} {...VALUE_LABEL_PROPS} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
};

export const SegmentCharts: React.FC<{ segments: SegmentPoint[]; currency: string | null }> = ({ segments, currency }) => {
  const { t } = useTranslation();
  const underFiveHundred = segments.some((s) => s.lowSample);
  return (
    <div>
      <div className="grid gap-4 md:grid-cols-3">
        <SegmentChart metric="share" title={t('channels.breakdown.segmentShare')} bars={barsOf(segments, 'share', t)} format={(n) => fmtPct(n, 1)} />
        <SegmentChart metric="ctr" title={t('channels.breakdown.segmentCtr')} bars={barsOf(segments, 'ctr', t)} format={(n) => fmtPct(n)} />
        <SegmentChart metric="cpc" title={t('channels.breakdown.segmentCpc')} bars={barsOf(segments, 'cpc', t)} format={(n) => fmtMoney(n, currency)} />
      </div>
      {underFiveHundred && <p className="mt-2 text-xs text-neutral-700">{t('channels.breakdown.segmentLowSample')}</p>}
    </div>
  );
};
